"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

export type OptimisticMessage = {
  text: string;
  fileData: Array<{
    name: string;
    type: string;
    size: number;
    data: string; // base64
  }>;
  assistantId: string;
};

type TransferPayload = {
  message: OptimisticMessage;
  assistantId: string;
  orgId?: string;
};

type ChatTransferContextValue = {
  setOptimisticTransfer: (
    conversationId: string,
    payload: TransferPayload
  ) => void;
  consumeOptimisticTransfer: (conversationId: string) => TransferPayload | null;
  peekOptimisticTransfer: (conversationId: string) => TransferPayload | null;
};

const ChatTransferContext = createContext<ChatTransferContextValue | undefined>(
  undefined
);

export function ChatTransferProvider({ children }: { children: ReactNode }) {
  const transferStoreRef = useRef<Map<string, TransferPayload>>(new Map());

  const setOptimisticTransfer = useCallback(
    (conversationId: string, payload: TransferPayload) => {
      transferStoreRef.current.set(conversationId, payload);
    },
    []
  );

  const consumeOptimisticTransfer = useCallback((conversationId: string) => {
    const payload = transferStoreRef.current.get(conversationId) || null;
    if (payload) {
      transferStoreRef.current.delete(conversationId);
    }
    return payload;
  }, []);

  const peekOptimisticTransfer = useCallback((conversationId: string) => {
    return transferStoreRef.current.get(conversationId) || null;
  }, []);

  const value = useMemo(
    () => ({
      setOptimisticTransfer,
      consumeOptimisticTransfer,
      peekOptimisticTransfer,
    }),
    [setOptimisticTransfer, consumeOptimisticTransfer, peekOptimisticTransfer]
  );

  return (
    <ChatTransferContext.Provider value={value}>
      {children}
    </ChatTransferContext.Provider>
  );
}

export function useChatTransfer(): ChatTransferContextValue {
  const ctx = useContext(ChatTransferContext);
  if (!ctx) {
    throw new Error(
      "useChatTransfer must be used within a ChatTransferProvider"
    );
  }
  return ctx;
}
